// 엑셀 업로드 컴포넌트 (중복 회차 감지 및 저장 정책 선택 지원)
// src/pages/admin/components/draw/ExcelImporter.tsx

import type {
  ExcelPreviewRow,
  DbSummary,
  ExcelDuplicateMode,
  ExcelImportPlan,
} from '@/pages/admin/components/draw/types';

interface ExcelImporterProps {
  excelFile: File | null;
  excelPreview: ExcelPreviewRow[];
  excelParsing: boolean;
  excelImporting: boolean;
  excelImportProgress: string;
  excelDuplicateMode: ExcelDuplicateMode;
  excelImportPlan: ExcelImportPlan;
  duplicateChecking: boolean;
  duplicateCheckError: string;
  onDuplicateModeChange: (mode: ExcelDuplicateMode) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  dbSummary: DbSummary | null;
  autoSyncEnabled: boolean;
  error: string;
  success: string;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onImport: () => void;
  onClear: () => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ExcelImporter({
  excelFile,
  excelPreview,
  excelParsing,
  excelImporting,
  excelImportProgress,
  excelDuplicateMode,
  excelImportPlan,
  duplicateChecking,
  duplicateCheckError,
  onDuplicateModeChange,
  fileInputRef,
  dbSummary,
  autoSyncEnabled,
  onFileSelect,
  onImport,
  onClear,
}: ExcelImporterProps) {
  return (
    <div className="space-y-6">
      <div className="bg-background-50 rounded-2xl border border-background-200 p-4 md:p-6">
        <h4 className="font-heading text-sm text-foreground-700 mb-2">엑셀 파일로 당첨번호 가져오기</h4>
        <p className="text-xs text-foreground-400 mb-5">
          동행복권 회차별 당첨번호 엑셀 파일(.xlsx)을 업로드하면 자동으로 파싱하여 DB에 저장합니다.<br className="hidden md:inline" />
          <strong className="text-foreground-600">컬럼: No, 회차, 당첨번호(6개), 보너스, 순위, 당첨게임수, 1게임당 당첨금액</strong> 형식을 자동 인식합니다.
        </p>

        {/* File select area */}
        {!excelFile && (
          <div className="border-2 border-dashed border-background-300 rounded-xl p-6 md:p-8 text-center hover:border-primary-400 transition-colors">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={onFileSelect}
              className="hidden"
              id="excel-upload"
            />
            <label
              htmlFor="excel-upload"
              className="cursor-pointer flex flex-col items-center gap-3"
            >
              <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-file-excel-2-line text-2xl text-secondary-600"></i>
              </div>
              <div>
                <p className="text-sm text-foreground-700 font-medium">
                  엑셀 파일 선택하기
                </p>
                <p className="text-xs text-foreground-400 mt-1">
                  .xlsx 또는 .xls 파일(최대 10MB)을 선택해주세요
                </p>
              </div>
            </label>
          </div>
        )}

        {/* Parsing indicator */}
        {excelParsing && (
          <div className="border-2 border-background-200 rounded-xl p-6 md:p-8 text-center">
            <div className="w-10 h-10 mx-auto mb-3 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-foreground-600">엑셀 파일 파싱 중...</p>
            <p className="text-xs text-foreground-400 mt-1">데이터 양에 따라 몇 초 소요될 수 있습니다</p>
          </div>
        )}

        {/* Duplicate checking indicator */}
        {!excelParsing && duplicateChecking && (
          <div className="border-2 border-background-200 rounded-xl p-6 md:p-8 text-center">
            <div className="w-10 h-10 mx-auto mb-3 border-2 border-secondary-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-foreground-600">기존 DB 회차와 중복 여부 확인 중...</p>
          </div>
        )}

        {/* Duplicate check error */}
        {duplicateCheckError && (
          <div className="p-4 rounded-xl bg-accent-50 border border-accent-200 text-sm text-accent-700 mb-4">
            <p className="font-medium flex items-center gap-1.5">
              <i className="ri-error-warning-line text-accent-600"></i>
              {duplicateCheckError}
            </p>
            <p className="text-xs text-accent-600 mt-1">안전을 위해 데이터 저장이 차단되었습니다. 파일을 다시 선택해주세요.</p>
          </div>
        )}

        {/* File info + preview */}
        {excelFile && !excelParsing && !duplicateChecking && excelPreview.length > 0 && (
          <div>
            {/* File info bar */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-background-50 border border-background-200 mb-4 gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-excel-2-line text-xl text-secondary-600"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-foreground-800 font-medium truncate">{excelFile.name}</p>
                  <p className="text-xs text-foreground-400">{formatFileSize(excelFile.size)} · 전체 {excelPreview.length}건 파싱됨</p>
                </div>
              </div>
              <button
                onClick={onClear}
                className="text-xs text-foreground-400 hover:text-accent-600 cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                <i className="ri-close-line"></i> 취소
              </button>
            </div>

            {/* Classification Summary Badges */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-background-100 text-foreground-700 border border-background-200">
                전체 파싱: <strong>{excelImportPlan.totalCount}건</strong>
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-secondary-50 text-secondary-700 border border-secondary-200">
                <span className="w-2 h-2 rounded-full bg-secondary-500"></span>
                신규 회차: <strong>{excelImportPlan.newCount}건</strong>
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-accent-50 text-accent-700 border border-accent-200">
                <span className="w-2 h-2 rounded-full bg-accent-500"></span>
                기존 회차(중복): <strong>{excelImportPlan.existingCount}건</strong>
              </span>
            </div>

            {/* Duplicate Policy Selection */}
            <div className="p-3.5 rounded-xl bg-background-100/70 border border-background-200 mb-4 space-y-2">
              <p className="text-xs font-semibold text-foreground-700 flex items-center gap-1.5">
                <i className="ri-settings-3-line text-sm"></i>
                중복 회차 처리 정책
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
                <label className="flex items-center gap-2 text-xs text-foreground-800 cursor-pointer">
                  <input
                    type="radio"
                    name="excelDuplicatePolicy"
                    value="skip"
                    checked={excelDuplicateMode === 'skip'}
                    onChange={() => onDuplicateModeChange('skip')}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span>
                    <strong>기존 회차 건너뛰기</strong> <span className="text-foreground-400">(권장 기본값: 신규 {excelImportPlan.newCount}건만 저장)</span>
                  </span>
                </label>
                {excelImportPlan.existingCount > 0 && (
                  <label className="flex items-center gap-2 text-xs text-foreground-800 cursor-pointer">
                    <input
                      type="radio"
                      name="excelDuplicatePolicy"
                      value="overwrite"
                      checked={excelDuplicateMode === 'overwrite'}
                      onChange={() => onDuplicateModeChange('overwrite')}
                      className="text-accent-600 focus:ring-accent-500"
                    />
                    <span>
                      <strong className="text-accent-700">기존 회차 덮어쓰기</strong> <span className="text-accent-600">({excelImportPlan.existingCount}건 기존 정보 갱신)</span>
                    </span>
                  </label>
                )}
              </div>
            </div>

            {/* Preview table */}
            <div className="overflow-x-auto max-h-64 border border-background-200 rounded-xl">
              <table className="w-full text-sm min-w-[550px]">
                <thead className="sticky top-0 bg-background-50">
                  <tr className="border-b border-background-200">
                    <th className="text-left py-2.5 px-3 text-foreground-400 font-medium text-xs">상태</th>
                    <th className="text-left py-2.5 px-3 text-foreground-400 font-medium text-xs">회차</th>
                    <th className="text-left py-2.5 px-3 text-foreground-400 font-medium text-xs">추첨일</th>
                    <th className="text-left py-2.5 px-3 text-foreground-400 font-medium text-xs">당첨번호</th>
                    <th className="text-left py-2.5 px-3 text-foreground-400 font-medium text-xs">보너스</th>
                    <th className="text-left py-2.5 px-3 text-foreground-400 font-medium text-xs">1등 당첨자</th>
                    <th className="text-left py-2.5 px-3 text-foreground-400 font-medium text-xs">1등 당첨금</th>
                  </tr>
                </thead>
                <tbody>
                  {excelImportPlan.classifiedRows.slice(0, 10).map((row) => (
                    <tr key={row.id} className="border-b border-background-100">
                      <td className="py-2 px-3">
                        {row.status === 'new' ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-secondary-100 text-secondary-700">
                            신규
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-accent-100 text-accent-700">
                            기존
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-foreground-800 font-bold text-xs">{row.id}회</td>
                      <td className="py-2 px-3 text-foreground-500 text-xs">{row.draw_date}</td>
                      <td className="py-2 px-3">
                        <div className="flex gap-0.5">
                          {row.numbers.map((n) => (
                            <span key={n} className="inline-flex w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-[10px] items-center justify-center font-bold">
                              {n}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <span className="inline-flex w-5 h-5 rounded-full bg-accent-100 text-accent-700 text-[10px] items-center justify-center font-bold">
                          {row.bonus}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-foreground-600 text-xs">
                        {row.winners_1st ? `${row.winners_1st}명` : '-'}
                      </td>
                      <td className="py-2 px-3 text-foreground-600 text-xs">
                        {row.prize_1st ? `${(row.prize_1st / 100000000).toFixed(1)}억` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {excelImportPlan.classifiedRows.length > 10 && (
                <div className="text-center py-2 text-xs text-foreground-400 bg-background-50">
                  ... 외 {excelImportPlan.classifiedRows.length - 10}건 (전체 {excelImportPlan.classifiedRows.length}건 중 10건만 표시)
                </div>
              )}
            </div>

            {/* Zero new rows warning in skip mode */}
            {excelDuplicateMode === 'skip' && excelImportPlan.newCount === 0 && (
              <div className="mt-4 p-3 rounded-xl bg-background-100 border border-background-200 text-xs text-foreground-600 flex items-center gap-2">
                <i className="ri-information-line text-sm text-foreground-500"></i>
                <span>모든 회차가 이미 DB에 등록되어 있어 등록할 신규 회차가 없습니다. (기존 데이터를 수정하려면 '기존 회차 덮어쓰기'를 선택하세요)</span>
              </div>
            )}

            {/* Import button */}
            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={onImport}
                disabled={excelImporting || !excelImportPlan.canImport || !!duplicateCheckError || duplicateChecking}
                className="px-5 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {excelImporting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    가져오는 중...
                  </span>
                ) : (
                  <>
                    <i className="ri-upload-cloud-2-line mr-1.5"></i>
                    {excelDuplicateMode === 'skip'
                      ? `DB에 저장하기 (신규 ${excelImportPlan.targetCount}건)`
                      : `DB에 덮어쓰기 저장 (${excelImportPlan.targetCount}건)`}
                  </>
                )}
              </button>
            </div>

            {excelImportProgress && (
              <div className="mt-3 p-3 rounded-lg bg-background-100 border border-background-200">
                <p className="text-sm text-foreground-600 flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></span>
                  {excelImportProgress}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Missing data info */}
      <div className="bg-background-100/50 rounded-2xl border border-background-200 p-4 md:p-5">
        <h4 className="font-heading text-sm text-foreground-700 mb-3">현재 DB 현황</h4>
        {dbSummary ? (
          <div className="space-y-1">
            <p className="text-xs text-foreground-500">
              현재 등록된 회차: <strong className="text-foreground-800">{dbSummary.min}~{dbSummary.max}회</strong> ({dbSummary.count}건)
            </p>
            {dbSummary.min > 1 && (
              <p className="text-xs text-foreground-500">
                누락된 회차: <strong className="text-accent-600">1~{dbSummary.min - 1}회</strong> (총 {dbSummary.min - 1}건)
              </p>
            )}
            <p className="text-xs text-foreground-500">
              예상 최신 회차: <strong className="text-foreground-800">{(() => {
                const firstDraw = new Date('2002-12-07');
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const daysSinceSaturday = today.getDay() === 6 ? 0 : (today.getDay() + 1) % 7;
                const lastSaturday = new Date(today);
                lastSaturday.setDate(today.getDate() - daysSinceSaturday);
                const diffMs = lastSaturday.getTime() - firstDraw.getTime();
                return Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000)) + 1;
              })()}회</strong>
            </p>
            {autoSyncEnabled && (
              <p className="text-xs text-secondary-600 mt-2 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-secondary-500"></span>
                자동 동기화 활성화됨 — 매주 새로운 회차를 자동으로 확인합니다
              </p>
            )}
          </div>
        ) : (
          <p className="text-xs text-foreground-400">로딩 중...</p>
        )}
      </div>
    </div>
  );
}
